import { useEffect, useState } from 'react'
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react'
import { useI18n } from '../../lib/i18n'

/**
 * Client-side pagination for a list. `resetKey` jumps back to the first page
 * when it changes (e.g. a new search or filter), and the current page is clamped
 * so shrinking the list never leaves you on an empty page.
 */
export function usePaged<T>(items: T[], pageSize: number, resetKey?: unknown) {
  const [page, setPage] = useState(0)
  useEffect(() => { setPage(0) }, [resetKey])
  const total = items.length
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const current = Math.min(page, totalPages - 1)
  const start = current * pageSize
  const end = Math.min(start + pageSize, total)
  return { page: current, setPage, total, totalPages, start, end, pageItems: items.slice(start, end) }
}

/**
 * El gemelo de `usePaged` para listas que pagina el servidor.
 *
 * Devuelve la misma forma para que `<Pagination>` no tenga que distinguir. La
 * diferencia es que aquí no se corta ningún array: `total` lo dice el servidor
 * y las filas que llegan ya son la página. Eso es lo que permite tener miles de
 * registros sin descargarlos todos para enseñar diez.
 */
export function useServerPaged(total: number, pageSize: number, resetKey?: unknown) {
  const [page, setPage] = useState(0)
  useEffect(() => { setPage(0) }, [resetKey])
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const current = Math.min(page, totalPages - 1)
  const start = total === 0 ? 0 : current * pageSize
  const end = Math.min(start + pageSize, total)
  return { page: current, setPage, total, totalPages, start, end }
}

/** Espera a que dejes de teclear antes de pedir al servidor. */
export function useDebounced<T>(value: T, delay = 300): T {
  const [settled, setSettled] = useState(value)
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), delay)
    return () => clearTimeout(timer)
  }, [value, delay])
  return settled
}

/**
 * "Mostrando 1–12 de 100" + páginas y anterior/siguiente. Cuando hay muchas
 * páginas conserva los extremos y las cercanas a la actual; no convierte el
 * pie de una lista grande en una fila de decenas de botones.
 */
/*
 * Con una sola página no se enseña.
 *
 * El defecto era enseñarla siempre, y eso ponía «Mostrando 1–3 de 3 envíos» con
 * un único «1» debajo de tres filas: un mando de navegación que no navega a
 * ninguna parte, en todas las listas cortas del panel. Ahora aparece cuando
 * empieza a haber páginas, que es cuando sirve.
 */
export function Pagination({ page, totalPages, total, start, end, onPage, unit, alwaysShow = false }: {
  page: number
  totalPages: number
  total: number
  start: number
  end: number
  onPage: (page: number) => void
  unit?: string
  alwaysShow?: boolean
}) {
  const { t } = useI18n()
  const unitLabel = unit ?? t('common.items')
  if (total === 0) return null
  if (totalPages <= 1 && !alwaysShow) return null
  const pageItems: (number | 'ellipsis')[] = []
  if (totalPages <= 5) {
    for (let index = 0; index < totalPages; index += 1) pageItems.push(index)
  } else {
    const visible = [...new Set([0, page - 1, page, page + 1, totalPages - 1])]
      .filter((value) => value >= 0 && value < totalPages)
      .sort((a, b) => a - b)
    visible.forEach((value, index) => {
      const previous = visible[index - 1]
      if (previous !== undefined && value - previous > 1) pageItems.push('ellipsis')
      pageItems.push(value)
    })
  }
  return (
    <div className="dash-pagination">
      <span className="dash-pagination-info">
        {totalPages > 1
          ? t('common.pagination.showing', { start: start + 1, end, total, unit: unitLabel })
          : t('common.pagination.total', { total, unit: unitLabel })}
      </span>
      {totalPages > 1 ? (
        <div className="dash-pagination-nav">
          <button type="button" className="dash-pagination-btn" disabled={page === 0} onClick={() => onPage(page - 1)} aria-label={t('common.pagination.previous')}>
            <IconChevronLeft size={16} />
          </button>
          {pageItems.map((item, index) => item === 'ellipsis' ? (
            <span className="dash-pagination-ellipsis" aria-hidden="true" key={`ellipsis-${index}`}>…</span>
          ) : (
            <button
              type="button"
              className={`dash-pagination-number${item === page ? ' is-active' : ''}`}
              aria-label={t('common.pagination.page', { page: item + 1 })}
              aria-current={item === page ? 'page' : undefined}
              key={item}
              onClick={() => onPage(item)}
            >
              {item + 1}
            </button>
          ))}
          <button type="button" className="dash-pagination-btn" disabled={page >= totalPages - 1} onClick={() => onPage(page + 1)} aria-label={t('common.pagination.next')}>
            <IconChevronRight size={16} />
          </button>
        </div>
      ) : null}
    </div>
  )
}
